/**
 * The retired per-tab tournament routes.
 *
 * `/tournament/:id/matches` and `/tournament/:id/standings` were separate
 * lazily-loaded pages; their content now lives as sections of the hub, so both
 * resolve the slug and land on the corresponding anchor. Keeping the URLs
 * alive matters: they're in bookmarks, in shared messages, and in every link
 * the app itself sent out before this change.
 *
 * Query params are carried through — `?filter=my` is still read by the match
 * view inside the hub.
 */

import { Link, Navigate, useParams, useLocation } from 'react-router';
import { useTournament } from '../hooks';
import type { HubAnchor } from './tournamentHubLayout';

export function TournamentAnchorRedirect({ anchor }: { anchor: HubAnchor }) {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const location = useLocation();

  const { data: tournament, isLoading, error } = useTournament(tournamentId);

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="h-6 bg-bbx-dark-700 rounded w-32 animate-pulse" />
        <div className="h-40 bg-bbx-dark-800 rounded-card animate-pulse" />
      </div>
    );
  }

  if (error || !tournament?.slug) {
    return (
      <div className="bg-bbx-dark-800 rounded-card p-6 text-center">
        <h2 className="text-lg font-semibold text-bbx-dark-200 mb-2">Tournament not found</h2>
        <p className="text-bbx-dark-400 text-sm mb-4">
          This tournament may have been removed or the link is invalid.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 bg-bbx-primary-600 hover:bg-bbx-primary-500 text-white font-medium rounded-lg transition-colors min-h-tap"
        >
          Back to Tournaments
        </Link>
      </div>
    );
  }

  return <Navigate to={`/t/${tournament.slug}${location.search}#${anchor}`} replace />;
}

export function MatchesRedirect() {
  return <TournamentAnchorRedirect anchor="matches" />;
}

export function StandingsRedirect() {
  return <TournamentAnchorRedirect anchor="standings" />;
}
